import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { SpriteIcon } from '@/components/SpriteIcon';
import { AuthCard } from './AuthCard';

/** How long the page waits before sending the user home, as in the Razor script. */
const AUTO_REDIRECT_MS = 30_000;

/**
 * Replaces `Pages/Auth/Accessdenied.cshtml`.
 *
 * Shown when a user is signed in but not permitted — distinct from being signed
 * out, which `RequireAuth` sends to the login screen instead.
 *
 * The 30-second redirect home is kept, along with its escape hatch: any click
 * cancels it, so a user reading the page or heading for support is not yanked
 * away mid-action.
 */
export function AccessDeniedPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => navigate('/', { replace: true }), AUTO_REDIRECT_MS);
    const cancel = () => window.clearTimeout(timer);

    document.addEventListener('click', cancel);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', cancel);
    };
  }, [navigate]);

  return (
    <AuthCard
      centered
      showLogo={false}
      footer={
        <div className="text-center mt-4">
          <p className="text-white-50 small">
            {t(
              'auth:Auth.ContactAdministrator',
              'If you believe this is a mistake, contact your administrator.',
            )}
          </p>
        </div>
      }
    >
      {/* Drawn inline rather than pulled from the sprite, as the Razor page did:
          it is sized to 80px and is the only icon on the page at that scale. */}
      <div className="mb-4">
        <svg
          className="text-danger"
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>

      <h1 className="display-4 fw-bold text-danger mb-3">403</h1>
      <h2 className="h4 fw-bold mb-3">{t('auth:Auth.AccessDenied', 'Access Denied')}</h2>

      <p className="text-muted mb-4">
        {t('auth:Auth.AccessDeniedMessage', 'You do not have permission to access this page.')}
      </p>

      <div className="text-start mb-4 p-4 bg-body-tertiary rounded">
        <ul className="mb-0">
          <li className="mb-2">
            {t('auth:Auth.AccessDeniedReason1', 'Your account does not have the required role.')}
          </li>
          <li className="mb-2">
            {t('auth:Auth.AccessDeniedReason2', 'The page is restricted to specific permissions.')}
          </li>
          <li className="mb-2">
            {t('auth:Auth.AccessDeniedReason3', 'Your permissions were changed recently.')}
          </li>
          <li className="mb-0">
            {t('auth:Auth.AccessDeniedReason4', 'The link you followed is no longer valid.')}
          </li>
        </ul>
      </div>

      <div className="d-grid gap-2">
        <Link to="/" className="btn btn-primary btn-lg d-flex align-items-center justify-content-center">
          <SpriteIcon name="home" className="sa-icon me-2" />
          {t('auth:Auth.GoToHomepage', 'Go to Homepage')}
        </Link>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
        >
          <SpriteIcon name="arrow-left" className="sa-icon me-2" />
          {t('auth:Auth.GoBack', 'Go Back')}
        </button>
      </div>

      <div className="mt-4 pt-4 border-top">
        <p className="text-muted small mb-2">{t('auth:Auth.NeedHelp', 'Need help?')}</p>
        <div className="d-flex justify-content-center gap-3">
          {/* Both targets are MVC routes outside the SPA. */}
          <a href="/Help/Contact" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center">
            <SpriteIcon name="mail" size={14} className="sa-icon me-1" />
            {t('auth:Auth.ContactSupport', 'Contact Support')}
          </a>
          <a href="/Help/FAQ" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center">
            <SpriteIcon name="help-circle" size={14} className="sa-icon me-1" />
            {t('auth:Auth.FAQ', 'FAQ')}
          </a>
        </div>
      </div>
    </AuthCard>
  );
}
